import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock3, Plus, X } from "lucide-react";

const SERVICES = ["breakfast", "lunch", "dinner"];

// Operating hours: 6:00 AM – 9:00 PM (360 → 1260 in minutes)
const DAY_START = 6 * 60;   // 360
const DAY_END   = 21 * 60;  // 1260
const DAY_SPAN  = DAY_END - DAY_START; // 900 min = 15 hours
const MAX_SLOT_MIN = 15 * 60; // 15 hours

// ---- helpers ----
const pad = (n) => String(n).padStart(2, "0");
const minToHHMM = (m) => `${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`;
const hhmmToMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth()+1)}-${pad(x.getDate())}`; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const inOps = (m) => m >= DAY_START && m <= DAY_END;

export default function ChefAvailability({ chefs, customers, onAssigned }) {
  const today = ymd(new Date());
  const [chefId, setChefId] = useState("");
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // form now supports MULTIPLE slots per customer-chef mapping (each slot creates one mapping row).
  const [form, setForm] = useState({
    customer_id: "",
    slots: [{ start_time: "07:00", end_time: "08:00" }],
    services: ["breakfast"],
    end_date: today,
    address: "",
  });

  useEffect(() => {
    if (!chefId) return;
    let cancelled = false;
    setLoading(true);
    api.get("/admin/chef-availability", { params: { chef_id: chefId, date } })
      .then(({ data: resp }) => { if (!cancelled) setData(resp); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load availability"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chefId, date]);

  const load = useCallback(() => {
    if (!chefId) return;
    setLoading(true);
    api.get("/admin/chef-availability", { params: { chef_id: chefId, date } })
      .then(({ data: resp }) => setData(resp))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load availability"))
      .finally(() => setLoading(false));
  }, [chefId, date]);

  const opsBooked = useMemo(() => {
    if (!data) return [];
    return data.occupied
      .map(o => ({ ...o, start: Math.max(o.start_min, DAY_START), end: Math.min(o.end_min, DAY_END) }))
      .filter(o => o.end > o.start);
  }, [data]);

  const onCustomerChange = (id) => {
    const c = customers.find(x => x.id === id);
    setForm(f => ({ ...f, customer_id: id, address: c?.profile?.address || "" }));
  };

  const updateSlot = (i, patch) => setForm(f => ({ ...f, slots: f.slots.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addSlot = () => setForm(f => ({ ...f, slots: [...f.slots, { start_time: "07:00", end_time: "08:00" }] }));
  const removeSlot = (i) => setForm(f => ({ ...f, slots: f.slots.length > 1 ? f.slots.filter((_, idx) => idx !== i) : f.slots }));

  // Validate every slot; return { valid, errors: [msg], parsed: [{startMin, endMin, label}] }
  const slotEval = useMemo(() => {
    const parsed = form.slots.map((s) => {
      const startMin = hhmmToMin(s.start_time);
      const endMin = hhmmToMin(s.end_time);
      const dur = endMin - startMin;
      return { startMin, endMin, dur, label: `${s.start_time}-${s.end_time}` };
    });
    const errors = [];
    parsed.forEach((p, i) => {
      if (p.dur <= 0) errors.push(`Slot ${i + 1}: end time must be after start time`);
      else if (p.dur > MAX_SLOT_MIN) errors.push(`Slot ${i + 1}: duration ${(p.dur/60).toFixed(1)}h exceeds max 15 hours`);
      else if (!inOps(p.startMin) || !inOps(p.endMin)) errors.push(`Slot ${i + 1}: must be within 06:00–21:00`);
    });
    // duplicate slot values inside the batch
    const seen = new Set();
    parsed.forEach((p, i) => {
      if (seen.has(p.label)) errors.push(`Slot ${i + 1}: duplicate of another slot in this batch`);
      seen.add(p.label);
    });
    return { parsed, errors };
  }, [form.slots]);

  const conflicts = useMemo(() => {
    if (!data) return [];
    const list = [];
    slotEval.parsed.forEach((p, i) => {
      if (p.dur <= 0) return;
      data.occupied.forEach(o => {
        if (p.startMin < o.end_min && o.start_min < p.endMin) {
          list.push({ slotIdx: i, occ: o });
        }
      });
    });
    return list;
  }, [data, slotEval]);

  const assign = async (e) => {
    e.preventDefault();
    if (!chefId) { toast.error("Pick a chef first"); return; }
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    if (slotEval.errors.length) { toast.error(slotEval.errors[0]); return; }
    if (!form.services.length) { toast.error("Pick at least one service"); return; }
    if (conflicts.length) {
      const ok = window.confirm(`${conflicts.length} of your slot(s) overlap existing assignments. Assign anyway?`);
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const results = await Promise.allSettled(slotEval.parsed.map(p =>
        api.post("/admin/mappings", {
          customer_id: form.customer_id,
          chef_id: chefId,
          slot: p.label,
          service_types: form.services,
          start_date: date,
          end_date: form.end_date || date,
          customer_address: form.address,
        })
      ));
      const ok = results.filter(r => r.status === "fulfilled").length;
      const failed = results.map((r, i) => ({ r, i })).filter(x => x.r.status === "rejected");
      if (ok) toast.success(`${ok} slot${ok === 1 ? "" : "s"} assigned`);
      failed.forEach(({ r, i }) => {
        const msg = formatApiError(r.reason?.response?.data?.detail) || "Failed";
        toast.error(`Slot ${i + 1} (${slotEval.parsed[i].label}): ${msg}`);
      });
      if (ok) {
        setForm(f => ({ ...f, customer_id: "", slots: [{ start_time: "07:00", end_time: "08:00" }] }));
        load();
        onAssigned && onAssigned();
      }
    } finally { setSubmitting(false); }
  };

  // ---- Timeline geometry ----
  const pct = (m) => `${((clamp(m, DAY_START, DAY_END) - DAY_START) / DAY_SPAN) * 100}%`;
  const width = (s, e) => `${((clamp(e, DAY_START, DAY_END) - clamp(s, DAY_START, DAY_END)) / DAY_SPAN) * 100}%`;

  const HOUR_TICKS = [];
  for (let h = 6; h <= 21; h++) HOUR_TICKS.push(h);

  const totalFree = DAY_SPAN - opsBooked.reduce((s, o) => s + (o.end - o.start), 0);
  const totalBooked = opsBooked.reduce((s, o) => s + (o.end - o.start), 0);

  return (
    <div className="space-y-6">
      <div className="card-default">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Clock3 size={18} className="text-primary"/>
              <h3 className="heading text-xl">Chef Assignment</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Operating hours: 6:00 AM – 9:00 PM. Booked time in red, free time in green.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-56">
              <Select value={chefId} onValueChange={setChefId}>
                <SelectTrigger data-testid="avail-chef"><SelectValue placeholder="Select a chef"/></SelectTrigger>
                <SelectContent>
                  {chefs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input type="date" value={date} onChange={e=>setDate(e.target.value)} className="w-44" data-testid="avail-date"/>
            <Button size="sm" variant="ghost" onClick={()=>setDate(today)} data-testid="avail-today">Today</Button>
            <Button size="sm" variant="outline" onClick={()=>setDate(ymd(addDays(date, -1)))}>−1d</Button>
            <Button size="sm" variant="outline" onClick={()=>setDate(ymd(addDays(date, 1)))}>+1d</Button>
          </div>
        </div>

        {!chefId ? (
          <p className="text-sm text-muted-foreground py-12 text-center">Pick a chef above to see their 6 AM – 9 PM availability.</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground py-12 text-center">Loading…</p>
        ) : (
          <>
            {/* TIMELINE ----------------------------------------------------- */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{date}</span>
                <span><b className="text-emerald-700">{totalFree} min free</b> · <b className="text-rose-700">{totalBooked} min booked</b> · {opsBooked.length} assignment{opsBooked.length === 1 ? "" : "s"}</span>
              </div>

              <div className="relative h-20 rounded-2xl bg-emerald-50 border border-emerald-200 overflow-hidden">
                {HOUR_TICKS.map(h => (
                  <div key={h}
                    className="absolute top-0 bottom-0 border-l border-emerald-100"
                    style={{ left: pct(h * 60) }}
                  />
                ))}

                {opsBooked.map((seg, i) => (
                  <div
                    key={i}
                    className="absolute top-2 bottom-2 rounded-md bg-rose-500/85 border border-rose-700 cursor-pointer flex items-center justify-center text-[10px] font-medium text-white px-1.5 overflow-hidden whitespace-nowrap"
                    style={{ left: pct(seg.start), width: width(seg.start, seg.end) }}
                    onMouseEnter={()=>setHover({ i, seg })}
                    onMouseLeave={()=>setHover(null)}
                    data-testid={`bar-booked-${i}`}
                    title={`${minToHHMM(seg.start)} – ${minToHHMM(seg.end)} · ${seg.customer_name}`}
                  >
                    {seg.end - seg.start >= 45 ? seg.customer_name : ""}
                  </div>
                ))}

                {/* draft previews — one per valid slot */}
                {slotEval.parsed.map((p, i) => {
                  if (p.dur <= 0 || p.dur > MAX_SLOT_MIN || !inOps(p.startMin) || !inOps(p.endMin)) return null;
                  const isConflict = conflicts.some(c => c.slotIdx === i);
                  return (
                    <div
                      key={`draft-${i}`}
                      className={`absolute top-0 bottom-0 ${isConflict ? "bg-rose-500/40 border-rose-700" : "bg-amber-400/45 border-amber-600"} border-2 border-dashed rounded-md pointer-events-none`}
                      style={{ left: pct(p.startMin), width: width(p.startMin, p.endMin) }}
                      data-testid={`bar-draft-${i}`}
                    />
                  );
                })}
              </div>

              <div className="relative h-5">
                {HOUR_TICKS.map(h => (
                  <div key={h}
                    className={`absolute -translate-x-1/2 text-[10px] tabular-nums ${h % 3 === 0 ? "text-foreground font-semibold" : "text-muted-foreground"}`}
                    style={{ left: pct(h * 60) }}
                  >
                    {h % 3 === 0 ? `${pad(h)}:00` : pad(h)}
                  </div>
                ))}
              </div>

              {hover && (
                <div className="text-xs px-3 py-2 rounded-lg bg-card border border-border inline-block">
                  <b>{hover.seg.customer_name}</b> · {minToHHMM(hover.seg.start)} – {minToHHMM(hover.seg.end)} · <span className="capitalize text-muted-foreground">{(hover.seg.service_types || []).join(", ")}</span>
                </div>
              )}

              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-300"/>Available</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-rose-500/85 border border-rose-700"/>Booked</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400/45 border border-amber-600 border-dashed"/>Draft</span>
              </div>
            </div>

            {/* ASSIGNMENT FORM ----------------------------------------- */}
            <form onSubmit={assign} className="mt-8 pt-6 border-t border-border space-y-4" data-testid="assign-form">
              <div>
                <h4 className="heading text-lg">Assign chef to a customer</h4>
                <p className="text-xs text-muted-foreground">Add one or more time slots. Each slot must be within 06:00–21:00 and at most 15 hours.</p>
              </div>

              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={onCustomerChange}>
                  <SelectTrigger data-testid="avail-customer"><SelectValue placeholder="Select customer"/></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Slots ({form.slots.length})</Label>
                  <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={addSlot} data-testid="avail-add-slot">
                    <Plus size={12} className="mr-1"/> Add slot
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.slots.map((s, i) => {
                    const p = slotEval.parsed[i];
                    const rowErr = slotEval.errors.find(e => e.startsWith(`Slot ${i + 1}:`));
                    return (
                      <div key={i} className="flex items-center gap-2" data-testid={`slot-row-${i}`}>
                        <span className="text-xs w-6 text-muted-foreground tabular-nums">#{i + 1}</span>
                        <Input type="time" min="06:00" max="21:00" value={s.start_time}
                          onChange={e => updateSlot(i, { start_time: e.target.value })}
                          className="w-32" data-testid={`avail-start-${i}`}/>
                        <span className="text-muted-foreground text-xs">→</span>
                        <Input type="time" min="06:00" max="21:00" value={s.end_time}
                          onChange={e => updateSlot(i, { end_time: e.target.value })}
                          className="w-32" data-testid={`avail-end-${i}`}/>
                        <Badge variant="outline" className="rounded-full text-[10px] tabular-nums">
                          {p && p.dur > 0 ? `${p.dur} min` : "—"}
                        </Badge>
                        {rowErr && <span className="text-[11px] text-destructive flex-1 truncate">{rowErr.replace(`Slot ${i + 1}: `, "")}</span>}
                        {form.slots.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" onClick={() => removeSlot(i)} data-testid={`avail-remove-slot-${i}`}>
                            <X size={14}/>
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Service type</Label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {SERVICES.map(s => {
                    const on = form.services.includes(s);
                    return (
                      <button type="button" key={s} data-testid={`avail-svc-${s}`}
                        onClick={()=>setForm(f=>({...f, services: on ? f.services.filter(x=>x!==s) : [...f.services, s]}))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Recurring until</Label>
                  <Input type="date" value={form.end_date} min={date} onChange={e=>setForm(f=>({...f, end_date: e.target.value}))} data-testid="avail-end-date"/>
                </div>
                <div>
                  <Label className="opacity-0">.</Label>
                  <div className="text-xs text-muted-foreground pt-2.5">
                    {date} → {form.end_date || date} daily · {form.slots.length} slot{form.slots.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label>Special instructions / address</Label>
                  <Textarea value={form.address} onChange={e=>setForm(f=>({...f, address: e.target.value}))} placeholder="Customer address & any prep notes" data-testid="avail-address"/>
                </div>
              </div>

              {slotEval.errors.length > 0 && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive space-y-0.5" data-testid="slot-errors">
                  {slotEval.errors.map((err, i) => <div key={i}>⚠ {err}</div>)}
                </div>
              )}
              {conflicts.length > 0 && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs">
                  <div className="font-semibold text-destructive mb-1">⚠ {conflicts.length} conflict{conflicts.length===1?"":"s"} with existing bookings</div>
                  {conflicts.map((c,i)=> (
                    <div key={i} className="text-destructive/80">Slot {c.slotIdx + 1} vs {minToHHMM(c.occ.start_min)} – {minToHHMM(c.occ.end_min)} · {c.occ.customer_name}</div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={submitting || slotEval.errors.length > 0} className="rounded-full bg-primary hover:bg-primary/90" data-testid="avail-assign-submit">
                  {submitting ? "Assigning…" : `Assign ${form.slots.length} slot${form.slots.length === 1 ? "" : "s"}`}
                </Button>
                <Badge variant="outline" className="rounded-full text-xs">Total {slotEval.parsed.reduce((s, p) => s + (p.dur > 0 ? p.dur : 0), 0)} min</Badge>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
